/**
 * LandingPersonaleTab — configurazione della landing pubblica `/p/:slug` dell'agente.
 *
 * Mostra:
 *   - URL pubblico + copy button
 *   - QR code preview + download
 *   - Welcome Kit PDF download
 *   - Input headline custom, intro, città principale
 *   - Toggle attiva/disattiva landing
 *
 * Endpoint: GET/POST /api/mandates/my-landing
 */
import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle, Globe, QrCode, Eye, EyeSlash, Image as ImageIcon, Link as LinkIcon, PencilSimple, WhatsappLogo, EnvelopeSimple, Share } from "@phosphor-icons/react";
import VolantinoStimaModal from "@/components/VolantinoStimaModal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingPersonaleTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [headline, setHeadline] = useState("");
  const [intro, setIntro] = useState("");
  const [city, setCity] = useState("");
  const [active, setActive] = useState(true);
  const [volantinoOpen, setVolantinoOpen] = useState(false);
  // iter v28 r252 — User M1170: l'utente vuole l'anteprima della pagina
  // del proprietario INLINE, non in una nuova tab. Mostriamo un iframe
  // dimensionato (mobile/desktop toggle) appena sotto al box hero.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("mobile");
  const previewRef = useRef(null);

  useEffect(() => {
    api.get(`/mandates/my-landing`)
      .then(r => {
        setConfig(r.data);
        setHeadline(r.data.headline || "");
        setIntro(r.data.intro || "");
        setCity(r.data.city || "");
        setActive(r.data.active ?? true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // iter 275 round 39: preferiamo SEMPRE window.location.origin invece di
  // public_url_full. Il backend ritorna public_url_full hardcoded a
  // prontoimmo.it (production) — in preview/staging punta a un dominio
  // diverso e l'utente vede "Pagina non disponibile". Usando l'origin
  // attuale, "Apri" funziona in qualsiasi ambiente.
  //
  // iter v28 r367 — User M1277: 1 sola superficie pubblica.
  // Pagina online ufficiale = `/stima/{referral_code}` (funnel anonimo,
  // form interattivo, brand sage Pronto). La vecchia `/p/{slug}` rimane
  // come fallback per chi ha link vecchi (redirect lato App.js).
  const referralCode = config?.referral_code || config?.code;
  // iter v29 r103 — Preferisci slug nome-cognome (`/stima/gianfilippo-baio`)
  // al codice random. Più riconoscibile come anteprima WhatsApp e
  // professionale per il cliente.
  const agentName = config?.agent_name || config?.name || "";
  const nameSlug = agentName
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  // iter v30 r144 — Slug `/s/{slug}` (single-word OK).
  const linkSegment = nameSlug || referralCode;
  const publicUrl = linkSegment
    ? `${window.location.origin}/stima/${linkSegment}`
    : (config ? `${window.location.origin}${config.public_url}` : "");
  // shareUrl punta a /s/{slug} (alias di /api/share/stima/) che attiva
  // l'anteprima OG dinamica con logo dell'agente quando il link viene
  // incollato in WhatsApp/Telegram/etc. I bot vedono OG meta; gli umani
  // vengono redirezionati al canonical /stima/{code} via 302.
  const shareUrl = linkSegment
    ? `${window.location.origin}/api/s/${linkSegment}`
    : publicUrl;

  // iter v28 r371 — User M1281: bottoni "condividi direttamente" + Web Share API.
  // iter v29 r240 — User M1396: testo aggiornato → niente "privacy garantita",
  // sostituito con copy esplicito "Niente nome, niente numero di telefono, 100% anonimo".
  const shareWhatsApp = () => {
    if (!shareUrl) return;
    const txt = `Ciao, curioso di sapere quanto vale la tua casa? Te lo dico in 30 secondi. Niente nome, niente numero di telefono, 100% anonimo:\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };
  const shareEmail = () => {
    if (!shareUrl) return;
    const subject = encodeURIComponent("Quanto vale casa tua?");
    const body = encodeURIComponent(`Ciao,\n\nse vuoi sapere quanto vale casa tua, ti mando il mio strumento di stima rapida: 30 secondi, niente nome, niente numero di telefono, 100% anonimo.\n\n${shareUrl}\n\nUn saluto.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const shareNative = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "La mia pagina stima",
          text: "Curioso di sapere quanto vale la tua casa? Te lo dico in 30 secondi. Niente nome, niente numero di telefono, 100% anonimo.",
          url: shareUrl,
        });
      } catch { /* utente ha annullato */ }
    }
  };
  const supportsNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/mandates/my-landing`, {
        headline: headline.trim() || null,
        intro: intro.trim() || null,
        city: city.trim() || null,
        active,
      });
      toast.success("Pagina personale aggiornata");
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    // iter v29 r242 — Copia SEMPRE l'URL share (/api/share/stima/), anche se
    // in UI mostriamo l'URL "umano" (/stima/). Il share URL attiva
    // l'anteprima OG personalizzata (logo + nome agente) quando incollato
    // in WhatsApp/Telegram/iMessage. Gli umani vengono redirezionati al
    // canonical /stima/{code} via 302.
    navigator.clipboard.writeText(shareUrl || publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // iter 275 round 39: il blob+<a download> NON funziona su iOS Safari (il
  // browser ignora silenziosamente l'attributo download per blob URL). Su iOS
  // l'immagine si apre in una nuova tab e l'utente fa "Salva su Foto" col
  // long-press. Su desktop il download nativo funziona normalmente.
  const downloadQr = async () => {
    try {
      const r = await api.get("/mandates/qr.png", { responseType: "blob" });
      const blobUrl = URL.createObjectURL(r.data);
      const filename = `qr-pronto-${config?.slug || "agente"}.png`;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      if (isIOS) {
        // Apre in nuova tab: l'utente vede il QR a tutto schermo e può
        // salvarlo dal menu Safari (Condividi → Salva immagine).
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        toast.success("Tap-and-hold sul QR → 'Salva su Foto'");
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("QR scaricato");
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 8000);
    } catch (e) {
      toast.error("Errore scaricamento QR code");
    }
  };

  if (loading) return <div className="mt-8 text-[#6B6A65] italic">Caricamento…</div>;
  if (!config) return <div className="mt-8 text-red-600">Errore caricamento landing</div>;

  return (
    <div className="mt-8 space-y-5" data-testid="landing-tab">

      {/* iter v28 r369 — User M1279: hero ristrutturato per chiarezza.
          Prima: URL serif gigante senza label → non chiaro che fosse "il link".
          Ora: chip "Link da condividere" + URL pill cliccabile + identità
          agente (logo agenzia + nome) per dare ancoraggio visivo. */}
      <div className="bg-[#1C1C1A] text-white rounded-2xl p-5 sm:p-6" data-testid="landing-hero">
        <div className="flex items-start gap-3 mb-4">
          {/* iter v28 r373 — User M1283: nel box scuro UI il logo agenzia
              renderizzato in B/W diventa una macchia nera invisibile sul
              fondo nero. Nei pannelli UI (non materiali stampabili) lascio
              il logo a colori NATURALI. La regola B/W vale solo per
              volantino/social/brochure dove serve coerenza editoriale.
              iter v28 r433 — User M1345: revertito. L'utente vuole il logo
              SEMPRE in nero anche nel box scuro. Il logo agenzia è dentro
              un cerchio BIANCO, quindi nero su bianco si vede bene. */}
          {config?.agency_logo_url ? (
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 p-1.5 overflow-hidden">
              <img
                src={config.agency_logo_url}
                alt={config.agency_name || ""}
                crossOrigin="anonymous"
                className="max-w-full max-h-full object-contain"
                style={{ filter: "brightness(0)" }}
                data-testid="landing-hero-agency-logo"
              />
            </div>
          ) : (
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center flex-shrink-0 font-bold text-base">
              {(config?.agency_name || config?.agent_name || "A").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[#A8B89A]">
              <Globe size={13} weight="duotone" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold">Versione online</span>
            </div>
            <div className="text-[14px] sm:text-[15px] font-semibold text-white mt-1 truncate" data-testid="landing-agent-name">
              {config?.agency_name || config?.agent_name || "La tua pagina"}
            </div>
            {config?.agent_name && config?.agency_name && (
              <div className="text-[11.5px] text-white/60 truncate">{config.agent_name}</div>
            )}
          </div>
        </div>

        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#A8B89A] mb-1.5">
          Link da condividere
        </div>
        <div className="bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 flex items-center gap-2 mb-3">
          <LinkIcon size={13} className="text-[#A8B89A] flex-shrink-0" />
          <code className="text-[12.5px] sm:text-[13.5px] text-white truncate flex-1" data-testid="public-url">{publicUrl}</code>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={copy} className="text-xs px-3.5 py-2 rounded-md bg-white text-[#1C1C1A] hover:bg-[#F1F4ED] font-semibold flex items-center gap-1.5" data-testid="copy-url">
            {copied ? <><CheckCircle size={13} weight="fill" /> Copiato!</> : <><Copy size={13} weight="bold" /> Copia link</>}
          </button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="hidden text-xs px-3.5 py-2 rounded-md bg-white/10 hover:bg-white/20 font-semibold flex items-center gap-1.5" data-testid="open-url-hidden">
            Apri esterno
          </a>
          <button
            onClick={() => {
              const next = !previewOpen;
              setPreviewOpen(next);
              if (next) {
                setTimeout(() => previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
              }
            }}
            className="text-xs px-3.5 py-2 rounded-md bg-white/10 hover:bg-white/20 font-semibold flex items-center gap-1.5"
            data-testid="toggle-preview"
            aria-pressed={previewOpen}
          >
            {previewOpen
              ? <><EyeSlash size={13} weight="bold" /> Chiudi anteprima</>
              : <><Eye size={13} weight="bold" /> Anteprima</>}
          </button>
          <button onClick={downloadQr} className="text-xs px-3.5 py-2 rounded-md bg-white/10 hover:bg-white/20 font-semibold flex items-center gap-1.5" data-testid="download-qr">
            <QrCode size={13} weight="bold" /> QR.png
          </button>
        </div>

        {/* iter v28 r371 — User M1281: «dammi la possibilità di inviarlo
            direttamente». 3 CTA di share esplicite (WhatsApp, Email, e
            Web Share API se supportata dal device → menu nativo iOS/Android). */}
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#A8B89A] mb-2">
            Condividi adesso
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={shareWhatsApp}
              className="bg-[#25D366] text-white py-2.5 rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:opacity-90"
              data-testid="share-wa"
            >
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </button>
            <button
              type="button"
              onClick={shareEmail}
              className="bg-white/15 text-white py-2.5 rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-white/25"
              data-testid="share-email"
            >
              <EnvelopeSimple size={14} weight="bold" /> Email
            </button>
            {supportsNativeShare && (
              <button
                type="button"
                onClick={shareNative}
                className="bg-white/15 text-white py-2.5 rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-white/25 col-span-2 sm:col-span-1"
                data-testid="share-native"
              >
                <Share size={14} weight="bold" /> Altre app
              </button>
            )}
          </div>
        </div>

        {/* iter v28 r361 — User M1270: «scarica il volantino stima, fai vedere di più».
            Promosso da pill nascosta nella riga delle azioni a CTA isolato e
            grande, sotto le azioni secondarie. Visibilità +100%.
            iter v28 r361 fix — User M1271: bg = #7C8B6E (verde logo Pronto).
            iter v28 r367 — id ancora per scroll dal sub-tab "Volantino". */}
        <button
          id="sezione-volantino"
          onClick={() => setVolantinoOpen(true)}
          className="mt-4 scroll-mt-20 w-full bg-[#7C8B6E] text-white hover:bg-[#6A7860] py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-[0_4px_14px_-4px_rgba(124,139,110,0.6)]"
          data-testid="download-volantino"
        >
          <ImageIcon size={18} weight="bold" /> Scarica volantino stima
          <span className="text-[10.5px] font-medium opacity-80 ml-1">A5 + Story</span>
        </button>
        <p className="text-xs text-white/60 mt-3 leading-relaxed">
          Il <strong className="text-white">volantino stima</strong> è il tuo strumento principale: A5 stampabile + PNG per Instagram story con il tuo logo agenzia.
        </p>
      </div>

      {/* iter v28 r252 — Anteprima inline della pagina del proprietario */}
      {previewOpen && (
        <div
          ref={previewRef}
          className="card-flat overflow-hidden"
          data-testid="landing-preview-embed"
        >
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#EAE7DF] bg-[#FAFAF7]">
            <div className="flex items-center gap-2 min-w-0">
              <Eye size={14} weight="bold" className="text-[#7C8B6E] flex-shrink-0" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D] truncate">
                Anteprima · come la vede il proprietario
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => setPreviewDevice("mobile")}
                className={`text-[11px] px-2.5 py-1 rounded-md font-semibold transition-colors ${
                  previewDevice === "mobile"
                    ? "bg-[#1C1C1A] text-white"
                    : "bg-white border border-[#EAE7DF] text-[#1C1C1A] hover:border-[#7C8B6E]"
                }`}
                aria-pressed={previewDevice === "mobile"}
                data-testid="preview-device-mobile"
              >
                Mobile
              </button>
              <button
                type="button"
                onClick={() => setPreviewDevice("desktop")}
                className={`text-[11px] px-2.5 py-1 rounded-md font-semibold transition-colors ${
                  previewDevice === "desktop"
                    ? "bg-[#1C1C1A] text-white"
                    : "bg-white border border-[#EAE7DF] text-[#1C1C1A] hover:border-[#7C8B6E]"
                }`}
                aria-pressed={previewDevice === "desktop"}
                data-testid="preview-device-desktop"
              >
                Desktop
              </button>
            </div>
          </div>
          <div className="bg-[#E9EAE4] p-4 sm:p-6 flex justify-center">
            <div
              className="bg-white rounded-[18px] shadow-[0_18px_48px_-18px_rgba(28,28,26,0.30),0_4px_14px_-4px_rgba(28,28,26,0.10)] overflow-hidden transition-all"
              style={
                previewDevice === "mobile"
                  ? { width: 390, maxWidth: "100%", height: 720 }
                  : { width: "100%", maxWidth: 1100, height: 700 }
              }
            >
              <iframe
                key={`${publicUrl}-${previewDevice}`}
                src={publicUrl}
                title="Anteprima pagina del proprietario"
                className="w-full h-full border-0 block"
                loading="lazy"
                data-testid="landing-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {/* iter v28 r369 — User M1279: titolo "Personalizza il messaggio" era
          ambiguo (di cosa? volantino? mail?). Esplicito: titolo + testi
          della pagina online. */}
      <div className="card-flat p-8 space-y-5" data-testid="landing-edit-texts">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F1F4ED] text-[#7C8B6E] flex items-center justify-center flex-shrink-0 mt-0.5">
            <PencilSimple size={18} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">Versione online</div>
            <h2 className="font-editorial text-xl sm:text-2xl text-[#1C1C1A] mt-0.5">Cosa appare sulla pagina</h2>
            <p className="text-[13px] text-[#3A3A36] mt-1.5 leading-relaxed max-w-2xl">
              Titolo, sottotitolo e città mostrati ai proprietari quando aprono la <strong>versione online</strong>. Se lasci vuoto, mostriamo testi di default.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold text-[#6B6A65] mb-2">
            Titolo principale
          </label>
          <input
            type="text"
            value={headline}
            onChange={e => setHeadline(e.target.value)}
            placeholder="Es. Vendere casa a Milano? Stima gratuita in 30 secondi"
            maxLength={200}
            className="w-full border border-[#E5E7E0] rounded-lg px-4 py-3 focus:outline-none focus:border-[#7C8B6E]"
            data-testid="headline-input"
          />
          <p className="text-[11px] text-[#9A968B] mt-1">Lascia vuoto per: {"\"Quanto vale davvero la tua casa?\""}</p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold text-[#6B6A65] mb-2">
            Sottotitolo / introduzione <span className="font-normal lowercase text-[#9A968B] tracking-normal">· opzionale</span>
          </label>
          <textarea
            value={intro}
            onChange={e => setIntro(e.target.value)}
            placeholder="Es. Sono Ada, lavoro a Brera da 15 anni. La stima è gratuita, basata su dati OMI ufficiali, senza impegno."
            maxLength={500}
            rows={3}
            className="w-full border border-[#E5E7E0] rounded-lg px-4 py-3 focus:outline-none focus:border-[#7C8B6E]"
            data-testid="intro-input"
          />
          <p className="text-[11px] text-[#9A968B] mt-1">Se vuoto, la pagina mostra solo il titolo e il form, senza testo introduttivo.</p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold text-[#6B6A65] mb-2">
            Città principale (per SEO + valutazione)
          </label>
          <input
            type="text"
            value={city}
            onChange={e => setCity(e.target.value)}
            placeholder="Es. Milano"
            maxLength={80}
            className="w-full border border-[#E5E7E0] rounded-lg px-4 py-3 focus:outline-none focus:border-[#7C8B6E]"
            data-testid="city-input"
          />
        </div>

        <div className="flex items-center justify-between gap-3 p-4 bg-[#FAFAF7] rounded-lg border border-[#E5E7E0]">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm">Landing attiva</div>
            <div className="text-xs text-[#6B6A65] mt-1">
              Se disattivi, il link mostra {"\"Pagina non disponibile\""} — nuove trattative non arrivano.
            </div>
          </div>
          {/* iter v33 r61 · User: «Sistema pulsante attiva come gli altri
              non ovale in verticale». Il Radix Switch shadcn si deformava
              su iOS Safari produzione (stesso bug gia' risolto in
              VetrinaAgente.jsx con `PillToggle` inline). Uso lo stesso
              pattern: bottone con inline styles espliciti, robusto contro
              qualunque override CSS globale o Tailwind purge. */}
          <button
            type="button"
            role="switch"
            aria-checked={active}
            onClick={() => setActive(!active)}
            data-testid="toggle-active"
            style={{
              display: "inline-flex",
              alignItems: "center",
              boxSizing: "border-box",
              width: 52,
              height: 26,
              minWidth: 52,
              minHeight: 26,
              borderRadius: 9999,
              background: active ? "#7C8B6E" : "#D9D6CE",
              position: "relative",
              flexShrink: 0,
              alignSelf: "center",
              cursor: "pointer",
              border: "none",
              padding: 0,
              transition: "background 0.18s ease-out",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 3,
                left: active ? 28 : 3,
                width: 20,
                height: 20,
                borderRadius: 9999,
                background: "#FFFFFF",
                boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                transition: "left 0.18s ease-out",
              }}
            />
          </button>
        </div>

        <div className="flex justify-end pt-3 border-t border-[#E5E7E0]">
          <button
            onClick={save}
            disabled={saving}
            className="px-6 py-2.5 bg-[#7C8B6E] text-white rounded-lg font-semibold disabled:opacity-50 hover:bg-[#6A7A5E]"
            data-testid="save-landing"
          >{saving ? "Salvataggio…" : "Salva modifiche"}</button>
        </div>
      </div>

      <VolantinoStimaModal open={volantinoOpen} onClose={() => setVolantinoOpen(false)} />
    </div>
  );
}
